import React, { FC } from "react";
import { FormikConfig, FormikProvider, useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { ModalWindow } from "src/components/ModalWindow";
import { RenderFormFields } from "src/components/RenderFormFields";
import { IPasswordUpdateValues } from "src/@types/auth";
import {
  PASSWORD_UPDATE_FORM_FIELDS,
  PASSWORD_UPDATE_VALIDATION_SCHEMA,
} from "./constants";
import { passwordUpdatePopupTrns } from "./trns";
import { AuthFormButton } from "../AuthFormButton";
import { useQueryParams } from "src/hooks/useQueryParams";
import { useAppDispatch } from "src/hooks/redux";
import { updatePasswordAsync } from "src/redux/auth/actions";
import { IS_ACTIVE_UPDATE_PASSWORD_KEY } from "src/components/Header/constants";
import { ModalState } from "src/hooks/useModal";
import { ModalWindowV2 } from "src/components/ModalWindowV2";

const TOKEN = "token";

const INITIAL_VALUES = {
  newPassword: "",
  repeatNewPassword: "",
};

interface Props extends ModalState {
  onOpenPasswordUpdatedPopup: VoidFunction;
}

export const PasswordUpdatePopup: FC<Props> = ({
  onOpenPasswordUpdatedPopup,
  ...modalProps
}) => {
  const { t } = useTranslation();
  const { getQueryParam, deleteQueryParam } = useQueryParams();
  const dispatch = useAppDispatch();
  const token = getQueryParam(TOKEN);

  const clearParams = () => {
    deleteQueryParam(TOKEN);
    deleteQueryParam(IS_ACTIVE_UPDATE_PASSWORD_KEY);
  };

  const formikProps: FormikConfig<IPasswordUpdateValues> = {
    initialValues: INITIAL_VALUES,
    validationSchema: PASSWORD_UPDATE_VALIDATION_SCHEMA,
    onSubmit: (values, { resetForm }) => {
      const valuesWithToken = { ...values, token };
      dispatch(
        updatePasswordAsync({
          values: valuesWithToken,
          onSuccess: () => {
            cleanup();
            onOpenPasswordUpdatedPopup();
          },
        })
      );
    },
  };

  const formik = useFormik<IPasswordUpdateValues>(formikProps);

  const cleanup = () => {
    modalProps.closeModal();
    formik.resetForm();
    clearParams();
  };

  return (
    <ModalWindowV2
      {...modalProps}
      closeModal={cleanup}
      className="max-w-80 w-full"
      isActiveCloseClickOutside={false}
      isShownCloseButton
      isActivePortal
    >
      <FormikProvider value={formik}>
        <form className="flex flex-col gap-9 p-5 bg-white-base border border-gray-base/20 rounded-20">
          <h3 className="font-semibold text-center">
            {t(passwordUpdatePopupTrns.title)}
          </h3>
          <div className="flex flex-col gap-13.25">
            <RenderFormFields fields={PASSWORD_UPDATE_FORM_FIELDS} />
          </div>
          <AuthFormButton>
            {t(passwordUpdatePopupTrns.updatePasswordButton)}
          </AuthFormButton>
        </form>
      </FormikProvider>
    </ModalWindowV2>
  );
};
